import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { CareerEntry, Committee, ExpenseStats, MemberBillVote, MemberDoc, MemberIndexEntry, MemberProposedBill, MemberRole, Party, QueryDoc } from '../types/types';
import Layout from '../components/Layout';
import RoleTag from '../components/RoleTag';
import { shortPartyName } from '../utils/party';
import { photoFor } from '../utils/names';
import { appearancesLabel } from '../utils/format';
import { useBreakpoint } from '../useBreakpoint';
import useUrlParams from '../hooks/useUrlParams';
import { previousKnesset, useScope } from '../hooks/useScope';
import PageContainer from '../layout/PageContainer';
import TwoPane from '../layout/TwoPane';
import {
  getAllMembers, getAllMembersOf, getCommittee, getCommitteeList, getExpenseStats, getMember, getMemberIndex,
  getMemberKnessetRecord, getMemberSummaries, getParties, getQueries, getVendor, MemberKnessetRecord, VendorCrossRef,
} from '../data/cache';
import { buttonReset, color, font, radius, space, TAP, text, tone, Tone, weight } from '../theme';
import DetailHeader from '../components/DetailHeader';
import { MOBILE_TOP_BAR_HEIGHT } from '../components/MobileShell';
import TabBar from '../components/TabBar';
import { Card, CardHeader, Section } from '../components/Card';
import { StatTile, StatTileRow } from '../components/StatTile';
import { Badge, StageBadge } from '../components/Badge';
import Bar, { SegmentBar } from '../components/Bar';
import ListRow from '../components/ListRow';
import Avatar from '../components/Avatar';
import Button, { ButtonLink } from '../components/Button';
import Chip, { ChipRow } from '../components/Chip';
import SearchField from '../components/SearchField';
import Icon, { IconName } from '../components/Icon';
import { EmptyState, ErrorState } from '../components/States';
import { Skeleton, SkeletonList, SkeletonText, SkeletonTiles } from '../components/Skeleton';
import { PresenceNowState, usePresenceMonth, usePresenceNow } from '../hooks/usePresence';
import MemberAgendaMotions from '../components/agenda/MemberAgendaMotions';
import {
  hoursLabel, hoursRank, isPresenceStale, isRanked, israelDay, monthLabel, monthTotals, PRESENCE_FIRST_MONTH,
  PRESENCE_SOURCE, PRESENCE_START, presenceDays, rankingIds, sinceLabel,
} from '../utils/presence';
import { israelToday, ymOf } from '../utils/schedule';

/* ── Tabs and deep links ─────────────────────────────────────────────────── */

type MkTab = 'bio' | 'career' | 'activity' | 'queries' | 'lobbying' | 'expenses';
const TAB_KEYS: MkTab[] = ['bio', 'career', 'activity', 'queries', 'lobbying', 'expenses'];

type ActivityFilter = 'all' | 'led' | 'passed' | 'inProgress' | 'committees' | 'chair' | 'attendance' | 'agendaMotions' | null;

// Callers pass `stat` (the tile the user pressed) to open the matching tab and filter
const statToTab = (stat: string | undefined): MkTab => {
  if (stat === 'queries') return 'queries';
  if (stat === 'lobbyists') return 'lobbying';
  if (stat === 'expenses') return 'expenses';
  if (stat === 'passed' || stat === 'inProgress' || stat === 'bills' || stat === 'attendance' || stat === 'passRate' || stat === 'committees' || stat === 'presence' || stat === 'agendaMotions') return 'activity';
  if (stat === 'seniority' || stat === 'newMks') return 'career';
  return 'bio';
};

const statToFilter = (stat: string | undefined): ActivityFilter => {
  if (stat === 'bills') return 'all';
  if (stat === 'passed' || stat === 'passRate') return 'passed';
  if (stat === 'inProgress') return 'inProgress';
  if (stat === 'attendance') return 'attendance';
  if (stat === 'committees') return 'committees';
  if (stat === 'agendaMotions') return 'agendaMotions';
  return null;
};

/* ── Helpers ─────────────────────────────────────────────────────────────── */

const fmtShekels = (n: number) => '₪' + Math.round(n).toLocaleString('he-IL');

const fmtDate = (iso: string | undefined): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('he-IL', { day: 'numeric', month: 'short', year: 'numeric' });
};

const sum = (xs: number[]) => xs.reduce((s, x) => s + x, 0);

// A committee as the MK page lists it, from meta/committeeIndex
interface CommitteeRow {
  id: string;
  name: string;
  type?: string;
  isChair: boolean;
  isCurrent?: boolean;
  lobbyistCount: number;
}

const COMMITTEE_TYPE_LABEL: Record<string, string> = { standing: 'קבועה', special: 'מיוחדת', joint: 'משותפת' };

// The header's fields, from the full doc or (while it loads) the member index
type HeaderSource = MemberDoc | MemberIndexEntry;

const tabPadding = (isDesktop: boolean) => (isDesktop ? 0 : `${space.md}px ${space.lg}px ${space.lg}px`);

const TabBody = ({ children }: { children: React.ReactNode }) => {
  const { isDesktop } = useBreakpoint();
  return <div style={{ padding: tabPadding(isDesktop), display: 'flex', flexDirection: 'column', gap: space.md }}>{children}</div>;
};

const Note = ({ children }: { children: React.ReactNode }) => (
  <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>{children}</div>
);

// A colored square and its label, under a bar
const Legend = ({ swatch, children }: { swatch: string; children: React.ReactNode }) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs + 2, fontSize: font.caption, color: color.textSecondary }}>
    <span style={{ width: 10, height: 10, borderRadius: 3, background: swatch, flexShrink: 0 }} />
    {children}
  </span>
);

// The header row of a card that opens and closes
const Disclosure = ({ open, onToggle, title, meta }: { open: boolean; onToggle: () => void; title: string; meta?: React.ReactNode }) => (
  <button
    type="button"
    aria-expanded={open}
    onClick={onToggle}
    style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: 52, padding: `${space.sm}px ${space.lg}px`, display: 'flex', alignItems: 'center', gap: space.sm }}
  >
    <span style={{ ...text.heading, flex: 1, minWidth: 0 }}>{title}</span>
    {meta !== undefined && <span style={{ fontSize: font.small, color: color.textSecondary }}>{meta}</span>}
    <span style={{ color: color.textTertiary }}><Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} /></span>
  </button>
);

// A neutral chip that only shows a fact (education, organizations)
const FactChip = ({ children }: { children: React.ReactNode }) => (
  <span style={{ fontSize: font.small, color: color.text, background: color.surfaceMuted, borderRadius: radius.md, padding: `${space.xs + 1}px ${space.md - 2}px`, lineHeight: 1.45 }}>
    {children}
  </span>
);

/* ── פרופיל ──────────────────────────────────────────────────────────────── */

const ageOf = (m: MemberDoc): number | null => {
  if (m.dateOfBirth) {
    const [d, mo, y] = m.dateOfBirth.split('/').map(Number);
    if (y) {
      const today = new Date();
      let a = today.getFullYear() - y;
      if (today.getMonth() + 1 < mo || (today.getMonth() + 1 === mo && today.getDate() < d)) a--;
      return a;
    }
  }
  return m.birthYear ? new Date().getFullYear() - m.birthYear : null;
};

const BIO_PREVIEW_CHARS = 240;

const ProfileTab = ({ m }: { m: MemberDoc }) => {
  const { isDesktop } = useBreakpoint();
  const [bioOpen, setBioOpen] = useState(false);
  const age = ageOf(m);

  const dob = m.dateOfBirth
    || (m.birthDate && typeof m.birthDate.seconds === 'number' ? new Date(m.birthDate.seconds * 1000).toLocaleDateString('he-IL') : null)
    || (m.birthYear ? String(m.birthYear) : null);
  const facts: { label: string; value: string; wide?: boolean }[] = [];
  if (dob) facts.push({ label: 'תאריך לידה', value: dob });
  if (m.placeOfBirth || m.birthCountry) facts.push({ label: 'מקום לידה', value: (m.placeOfBirth || m.birthCountry) as string });
  if (m.residence || m.birthPlace) facts.push({ label: 'מקום מגורים', value: (m.residence || m.birthPlace) as string });
  // Some docs store the Knesset API's English value
  const gender: string = m.gender;
  if (gender) facts.push({ label: 'מגדר', value: gender === 'female' ? 'נקבה' : gender === 'male' ? 'זכר' : gender });
  if (m.familyStatus) facts.push({ label: 'מצב משפחתי', value: m.familyStatus });
  if (m.languages && m.languages.trim()) facts.push({ label: 'שפות', value: m.languages.trim(), wide: true });

  const social = m.social || {};
  const links: { href: string; label: string; icon: IconName }[] = [];
  if (m.wikipediaUrl) links.push({ href: m.wikipediaUrl, label: 'ויקיפדיה', icon: 'document' });
  if (m.email) links.push({ href: `mailto:${m.email}`, label: m.email, icon: 'mail' });
  if (social.website || m.website) links.push({ href: (social.website || m.website) as string, label: 'אתר אישי', icon: 'globe' });
  if (social.facebook || m.facebook) links.push({ href: (social.facebook || m.facebook) as string, label: 'פייסבוק', icon: 'link' });
  if (social.twitter || m.twitter) links.push({ href: (social.twitter || m.twitter) as string, label: 'X (טוויטר)', icon: 'link' });
  if (social.instagram || m.instagram) links.push({ href: (social.instagram || m.instagram) as string, label: 'אינסטגרם', icon: 'link' });
  if (social.youtube || m.youtube) links.push({ href: (social.youtube || m.youtube) as string, label: 'יוטיוב', icon: 'video' });

  const bio = m.bio || '';
  const bioLong = bio.length > BIO_PREVIEW_CHARS;

  return (
    <TabBody>
      <StatTileRow>
        {age != null && <StatTile value={age} label="גיל" />}
        {m.knessetNumber ? <StatTile value={`כנסת ${m.knessetNumber}`} label="כהונה" /> : null}
        {m.children != null && <StatTile value={m.children} label="ילדים" />}
        {m.immigrationYear && <StatTile value={m.immigrationYear} label="שנת עלייה" />}
      </StatTileRow>

      {(facts.length > 0 || (m.education || []).length > 0 || m.militaryService || m.nationalService) && (
        <Card>
          {facts.length > 0 && (
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: isDesktop ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))', gap: `${space.md}px ${space.lg}px` }}>
              {facts.map(f => (
                <div key={f.label} style={{ gridColumn: f.wide ? '1 / -1' : undefined, minWidth: 0 }}>
                  <dt style={{ fontSize: font.caption, color: color.textSecondary }}>{f.label}</dt>
                  <dd style={{ margin: `${space.xxs}px 0 0`, fontSize: font.label, fontWeight: weight.medium, color: color.text }}>{f.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {(m.education || []).length > 0 && (
            <div style={{ borderTop: facts.length > 0 ? `1px solid ${color.divider}` : 'none', marginTop: facts.length > 0 ? space.lg : 0, paddingTop: facts.length > 0 ? space.lg : 0 }}>
              <CardHeader title="השכלה" style={{ marginBottom: space.sm }} />
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
                {(m.education || []).map((e, i) => (
                  <FactChip key={i}>{e.degree && e.institution ? `${e.degree} · ${e.institution}` : e.raw || e.institution}</FactChip>
                ))}
              </div>
            </div>
          )}

          {(m.militaryService || m.nationalService) && (
            <div style={{ borderTop: `1px solid ${color.divider}`, marginTop: space.lg, paddingTop: space.lg }}>
              <CardHeader title="שירות" style={{ marginBottom: space.sm }} />
              {m.militaryService && (
                <div>
                  <div style={{ fontSize: font.caption, color: color.textSecondary }}>שירות צבאי</div>
                  <div style={{ ...text.body, fontSize: font.label, whiteSpace: 'pre-wrap', marginTop: space.xxs }}>{m.militaryService}</div>
                </div>
              )}
              {m.nationalService && (
                <div style={{ marginTop: m.militaryService ? space.md : 0 }}>
                  <div style={{ fontSize: font.caption, color: color.textSecondary }}>שירות לאומי</div>
                  <div style={{ ...text.body, fontSize: font.label, whiteSpace: 'pre-wrap', marginTop: space.xxs }}>{m.nationalService}</div>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {bio && (
        <Card>
          <CardHeader title="ביוגרפיה" />
          <p id="mk-bio" style={{ ...text.body, margin: 0, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
            {bioOpen || !bioLong ? bio : bio.slice(0, BIO_PREVIEW_CHARS).trimEnd() + '…'}
          </p>
          {bioLong && (
            <Button variant="ghost" icon={bioOpen ? 'chevronUp' : 'chevronDown'} onClick={() => setBioOpen(!bioOpen)} style={{ marginTop: space.xs, paddingInline: 0 }}>
              {bioOpen ? 'הצגת פחות' : 'הצגת הביוגרפיה המלאה'}
            </Button>
          )}
        </Card>
      )}

      {m.plenumSeat && m.plenumSeatImageUrl && (
        <Card>
          <CardHeader title="מושב במליאה" meta={`מושב ${m.plenumSeat}`} />
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <img src={m.plenumSeatImageUrl} alt={`מפת המליאה, מושב ${m.plenumSeat}`} loading="lazy" decoding="async" style={{ width: 180, maxWidth: '100%', objectFit: 'contain' }} />
          </div>
        </Card>
      )}

      {links.length > 0 && (
        <Card padding={0}>
          <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}><CardHeader title="קישורים" style={{ marginBottom: 0 }} /></div>
          {links.map((l, i) => (
            <ListRow
              key={l.href}
              divider={i > 0}
              href={l.href}
              leading={<span style={{ color: color.primary }}><Icon name={l.icon} size={20} /></span>}
              title={<span style={{ direction: l.icon === 'mail' ? 'ltr' : undefined, unicodeBidi: 'isolate' }}>{l.label}</span>}
            />
          ))}
        </Card>
      )}
    </TabBody>
  );
};

/* ── קריירה ──────────────────────────────────────────────────────────────── */

const CAREER_TYPES: Record<string, { tone: Tone; label: string; accent: string }> = {
  pm: { tone: 'special', label: 'ראש ממשלה', accent: tone.special.fg },
  minister: { tone: 'info', label: 'שר/ה', accent: color.primary },
  'deputy-minister': { tone: 'info', label: 'סגן/ית שר/ה', accent: color.primary },
  leadership: { tone: 'info', label: 'תפקיד בכיר', accent: color.primary },
  'committee-chair': { tone: 'info', label: 'יו״ר ועדה', accent: color.primary },
  committee: { tone: 'neutral', label: 'ועדה', accent: color.border },
  mk: { tone: 'neutral', label: 'חבר/ת כנסת', accent: color.border },
};
const careerType = (type: string | undefined) => CAREER_TYPES[type || ''] || CAREER_TYPES.committee;

// Strip election slogans ("… לראשות הממשלה") and shorten the faction name for display
const normFaction = (name: string) => shortPartyName(name
  .replace(/\s*(?:[-–]\s*)?עם\s+[֐-׿ "'״\s]+לראשות\s+הממשלה/g, '')
  .replace(/\s*בהנהגת\s+[֐-׿ "'״\s]+לראשות\s+הממשלה/g, '')).trim();

// "שר האוצר" → "אוצר" for display (the full title stays the key)
const trimMinistryName = (role: string): string => {
  let name = role.replace(/^סגן\/ית\s+שר\/ה\s*[–-]\s*|^סגן\/ת\s+שר\s*[–-]\s*/, '').replace(/^שרת?\s+/, '').replace(/^סגנית?\s+שרת?\s+/, '').trim();
  if (name.startsWith('ה') && name.length > 1) name = name.slice(1);
  return name || role;
};

// Knesset numbers as text; only 3+ consecutive Knessets collapse into a range
const formatKnessets = (ks: number[]): string => {
  const sorted = ks.slice().sort((a, b) => a - b);
  const parts: string[] = [];
  let i = 0;
  while (i < sorted.length) {
    let j = i;
    while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) j++;
    if (j - i + 1 >= 3) parts.push(`כנסת ${sorted[i]}–${sorted[j]}`);
    else for (let k = i; k <= j; k++) parts.push(`כנסת ${sorted[k]}`);
    i = j + 1;
  }
  return parts.join(' · ');
};

const startYear = (c: CareerEntry): number => parseInt((c.years || '').split('–')[0], 10);

const CareerTab = ({ m }: { m: MemberDoc }) => {
  const [careerFilter, setCareerFilter] = useState<'minister' | 'chair' | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [ministriesOpen, setMinistriesOpen] = useState(false);
  // Which Knesset cards are open; by default only the current one
  const [openKnessets, setOpenKnessets] = useState<Record<number, boolean>>({});

  const career = m.careerHistory || [];
  if (career.length === 0) {
    return <TabBody><Card padding={0}><EmptyState icon="user" title="אין מידע על תפקידים" /></Card></TabBody>;
  }

  const byKnesset: Record<number, CareerEntry[]> = {};
  for (let i = 0; i < career.length; i++) {
    const k = career[i].knesset || 0;
    if (!byKnesset[k]) byKnesset[k] = [];
    byKnesset[k].push(career[i]);
  }
  const knessets = Object.keys(byKnesset).map(Number).sort((a, b) => b - a);
  const realKnessets = knessets.filter(k => k > 0);

  const years = career.map(startYear).filter(y => !isNaN(y));
  const yearsInPolitics = years.length > 0 ? new Date().getFullYear() - Math.min.apply(null, years) : null;
  const ministerEntries = career.filter(c => c.type === 'minister' || c.type === 'deputy-minister' || c.type === 'pm');
  const ministerKnessets = Array.from(new Set(ministerEntries.map(c => c.knesset || 0).filter(k => k > 0)));
  const chairKnessets = Array.from(new Set(career.filter(c => c.type === 'committee-chair').map(c => c.knesset || 0).filter(k => k > 0)));
  const factions = Array.from(new Set(career.map(c => (c.faction ? normFaction(c.faction) : '')).filter(Boolean)));
  const rolesCount = career.filter(c => c.type !== 'mk' && c.type !== 'committee').length;

  // Ministry → the Knessets it was held in (the PM's portfolios count too)
  const ministryMap: Record<string, number[]> = {};
  const addMinistry = (rawRole: string, k: number | undefined) => {
    // The same title sometimes comes with different spacing; count it once
    const role = (rawRole || '').trim().replace(/\s+/g, ' ');
    if (!role) return;
    if (!ministryMap[role]) ministryMap[role] = [];
    if (k && ministryMap[role].indexOf(k) < 0) ministryMap[role].push(k);
  };
  for (const c of ministerEntries) {
    addMinistry(c.role, c.knesset);
    if (c.type === 'pm' && Array.isArray(c.portfolios)) c.portfolios.forEach(p => addMinistry(p, c.knesset));
  }
  const ministries = Object.keys(ministryMap).map(r => [r, ministryMap[r]] as [string, number[]]).sort((a, b) => b[1].length - a[1].length);

  const isOpen = (k: number) => (Object.prototype.hasOwnProperty.call(openKnessets, k) ? openKnessets[k] : k === m.knessetNumber);
  const setAll = (open: boolean) => {
    const next: Record<number, boolean> = {};
    knessets.forEach(k => { next[k] = open; });
    setOpenKnessets(next);
  };
  const isRelevant = (k: number) => {
    if (selectedRoles.length > 0) return selectedRoles.some(r => (ministryMap[r] || []).indexOf(k) >= 0);
    if (careerFilter === 'minister') return ministerKnessets.indexOf(k) >= 0;
    if (careerFilter === 'chair') return chairKnessets.indexOf(k) >= 0;
    return true;
  };
  const toggleFilter = (f: 'minister' | 'chair') => { setCareerFilter(careerFilter === f ? null : f); setSelectedRoles([]); };
  const toggleRole = (r: string) => {
    setSelectedRoles(selectedRoles.indexOf(r) >= 0 ? selectedRoles.filter(x => x !== r) : selectedRoles.concat(r));
    setCareerFilter(null);
  };

  return (
    <TabBody>
      <Card padding={space.md}>
        <StatTileRow>
          <StatTile variant="flat" value={realKnessets.length} label="כנסות" />
          {yearsInPolitics ? <StatTile variant="flat" value={yearsInPolitics} label="שנות פעילות" /> : null}
          {ministerKnessets.length > 0 && (
            <StatTile variant="flat" value={ministerKnessets.length} label="כנסות כשר/ה" selected={careerFilter === 'minister'} indicator="filter" onClick={() => toggleFilter('minister')} />
          )}
          {chairKnessets.length > 0 && (
            <StatTile variant="flat" value={chairKnessets.length} label="כנסות כיו״ר ועדה" selected={careerFilter === 'chair'} indicator="filter" onClick={() => toggleFilter('chair')} />
          )}
          {factions.length > 1 && <StatTile variant="flat" value={factions.length} label="מפלגות" />}
        </StatTileRow>
      </Card>

      {ministries.length > 0 && (
        <Card padding={0}>
          <Disclosure open={ministriesOpen} onToggle={() => setMinistriesOpen(!ministriesOpen)} title="משרדים ממשלתיים" meta={ministries.length} />
          {ministriesOpen && (
            <div style={{ borderTop: `1px solid ${color.divider}`, padding: `${space.xs}px 0` }}>
              {ministries.map(([role, ks]) => {
                const selected = selectedRoles.indexOf(role) >= 0;
                return (
                  <button
                    key={role}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => toggleRole(role)}
                    style={{
                      ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: `${space.xs}px ${space.lg}px`,
                      display: 'flex', alignItems: 'center', gap: space.sm, background: selected ? color.primarySoft : 'transparent',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, background: selected ? color.primary : color.border }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: font.label, fontWeight: selected ? weight.bold : weight.regular, color: selected ? color.primary : color.text }}>{trimMinistryName(role)}</span>
                    <span style={{ fontSize: font.caption, color: selected ? color.primary : color.textSecondary, flexShrink: 0 }}>{formatKnessets(ks)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </Card>
      )}

      <Section
        title="היסטוריה פוליטית"
        action={
          <span style={{ display: 'flex', gap: space.xxs }}>
            <Button variant="ghost" onClick={() => setAll(true)} style={{ padding: `0 ${space.sm}px`, whiteSpace: 'nowrap' }}>פתיחת הכל</Button>
            <Button variant="ghost" onClick={() => setAll(false)} style={{ padding: `0 ${space.sm}px`, whiteSpace: 'nowrap' }}>סגירת הכל</Button>
          </span>
        }
      >
        {(rolesCount > 0 || !!yearsInPolitics) && (
          <div style={{ marginTop: -space.sm }}>
            <Note>{[rolesCount > 0 ? (rolesCount === 1 ? 'תפקיד אחד' : `${rolesCount} תפקידים`) : null, yearsInPolitics ? `${yearsInPolitics} שנים בפוליטיקה` : null].filter(Boolean).join(' · ')}</Note>
          </div>
        )}
        {knessets.map(k => {
          const items = byKnesset[k];
          const open = isOpen(k);
          // "כיום": the member sits in this Knesset now (not just: it's their latest)
          const isCurrent = k === m.knessetNumber && m.isCurrentMk === true;
          const mkEntry = items.find(i => i.type === 'mk');
          const faction = (mkEntry && mkEntry.faction) || (items.find(i => !!i.faction) || {}).faction;
          let span = mkEntry && mkEntry.years ? mkEntry.years : '';
          if (!span) {
            const starts = items.map(i => (i.years || '').split('–')[0]).filter(Boolean).sort();
            const ends = items.map(i => (i.years || '').split('–')[1]).filter(Boolean);
            const end = ends.indexOf('היום') >= 0 ? 'היום' : ends.sort().reverse()[0] || '';
            span = starts[0] ? `${starts[0]}–${end}` : '';
          }
          const topType = ['pm', 'minister', 'leadership', 'committee-chair'].filter(t => items.some(i => i.type === t))[0] || 'mk';
          const top = careerType(topType);
          return (
            <Card key={k} padding={0} style={{ opacity: isRelevant(k) ? 1 : 0.35, transition: 'opacity 0.2s', borderRight: `4px solid ${top.accent}` }}>
              <button
                type="button"
                aria-expanded={open}
                onClick={() => setOpenKnessets({ ...openKnessets, [k]: !open })}
                style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: 56, padding: `${space.md}px ${space.lg}px`, display: 'flex', alignItems: 'center', gap: space.md }}
              >
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: space.xs }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: font.body, fontWeight: weight.bold, color: color.text }}>{k > 0 ? `כנסת ${k}` : 'תפקידים נוספים'}</span>
                    {span && <span style={{ fontSize: font.small, color: color.textSecondary }}>{span}</span>}
                    {isCurrent && <Badge tone="success">כיום</Badge>}
                  </span>
                  {(faction || topType !== 'mk') && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
                      {faction && <span style={{ fontSize: font.small, color: color.textSecondary }}>{normFaction(faction)}</span>}
                      {topType !== 'mk' && <Badge tone={top.tone}>{top.label}</Badge>}
                    </span>
                  )}
                </span>
                <span style={{ color: color.textTertiary }}><Icon name={open ? 'chevronUp' : 'chevronDown'} size={18} /></span>
              </button>

              {open && (
                <div style={{ borderTop: `1px solid ${color.divider}`, padding: `${space.xs}px ${space.lg}px ${space.sm}px` }}>
                  {items.map((item, i) => {
                    const t = careerType(item.type);
                    if (item.type === 'pm') {
                      return (
                        <div key={i} style={{ background: tone.special.bg, borderRadius: radius.lg, padding: space.md, margin: `${space.sm}px 0` }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
                            <span style={{ fontSize: font.body, fontWeight: weight.bold, color: tone.special.fg, flex: 1, minWidth: 0 }}>{item.role}</span>
                            {item.isCurrent && <Badge tone="success">כיום</Badge>}
                            <span style={{ fontSize: font.caption, color: tone.special.fg, flexShrink: 0 }}>{item.years}</span>
                          </div>
                          {(item.portfolios || []).length > 0 && (
                            <div style={{ marginTop: space.sm }}>
                              <div style={{ fontSize: font.caption, color: tone.special.fg, marginBottom: space.xs }}>תיקים</div>
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs }}>
                                {(item.portfolios || []).map(p => (
                                  <span key={p} style={{ fontSize: font.caption, fontWeight: weight.medium, color: tone.special.fg, background: color.surface, borderRadius: radius.sm, padding: '2px 8px' }}>{trimMinistryName(p)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {item.govNum ? <div style={{ fontSize: font.caption, color: tone.special.fg, marginTop: space.sm }}>ממשלה {item.govNum}</div> : null}
                        </div>
                      );
                    }
                    const isMk = item.type === 'mk';
                    const meta = [item.govNum ? `ממשלה ${item.govNum}` : null, item.faction && !isMk ? normFaction(item.faction) : null].filter(Boolean).join(' · ');
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: space.sm, padding: `${space.sm + 2}px 0`, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          {(!isMk || item.isCurrent) && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: space.xs + 2, flexWrap: 'wrap', marginBottom: space.xs }}>
                              {!isMk && <Badge tone={t.tone}>{t.label}</Badge>}
                              {item.isCurrent && <Badge tone="success">כיום</Badge>}
                            </div>
                          )}
                          <div style={{ fontSize: font.label, fontWeight: isMk ? weight.regular : weight.medium, color: isMk ? color.textSecondary : color.text, lineHeight: 1.4 }}>{item.role}</div>
                          {meta && <div style={{ fontSize: font.caption, color: color.textSecondary, marginTop: space.xxs }}>{meta}</div>}
                        </div>
                        <span style={{ fontSize: font.caption, color: color.textSecondary, flexShrink: 0, paddingTop: 2 }}>{item.years}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </Section>
    </TabBody>
  );
};

/* ── פעילות ──────────────────────────────────────────────────────────────── */

const RESULT_BADGE: Record<string, { tone: Tone; label: string }> = {
  for: { tone: 'success', label: 'בעד' },
  against: { tone: 'danger', label: 'נגד' },
  abstain: { tone: 'neutral', label: 'נמנע' },
};
const READING_ORDER: Record<string, number> = { 'ראשונה': 1, 'שנייה': 2, 'שנייה ושלישית': 3, 'שלישית': 4 };
const RESULT_ORDER: Record<string, number> = { for: 0, abstain: 1, against: 2 };

type VoteSort = 'date' | 'reading' | 'result';
const VOTE_SORTS: { key: VoteSort; label: string }[] = [
  { key: 'date', label: 'תאריך' },
  { key: 'reading', label: 'קריאה' },
  { key: 'result', label: 'עמדה' },
];
const VOTES_PAGE = 10;
const BILLS_PREVIEW = 5;

const readStored = (key: string, fallback: string): string => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};
const writeStored = (key: string, value: string) => {
  try { localStorage.setItem(key, value); } catch { /* ok */ }
};

interface Comparisons { partyAvg: number | null; knessetAvg: number | null; rank: number | null; total: number }

const CommitteeList = ({ rows, from }: { rows: CommitteeRow[]; from: string }) => (
  <Card padding={0}>
    {rows.map((c, i) => (
      <ListRow
        key={c.id}
        divider={i > 0}
        to={`/committees/${c.id}`}
        state={{ from }}
        title={c.name}
        subtitle={[c.type ? `ועדה ${COMMITTEE_TYPE_LABEL[c.type] || ''}`.trim() : null, c.isCurrent === false ? 'הסתיימה' : null].filter(Boolean).join(' · ') || undefined}
        trailing={c.isChair ? <Badge tone="info">יו״ר</Badge> : undefined}
      />
    ))}
  </Card>
);

const BillRow = ({ bill, first, from }: { bill: MemberProposedBill; first: boolean; from: string }) => (
  <ListRow
    divider={!first}
    to={`/legislation/bill_${bill.billId}`}
    state={{ from }}
    title={<span style={{ color: bill.isStopped ? color.textSecondary : undefined }}>{bill.title}</span>}
    subtitle={
      <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
        <span style={{ display: 'flex', gap: space.xs, flexWrap: 'wrap' }}>
          <StageBadge stage={bill.simpleStage} />
          {bill.isPrimary && <Badge tone="info">יוזם/ת</Badge>}
          {bill.isBasicLaw && <Badge tone="special">חוק יסוד</Badge>}
          {!bill.isBasicLaw && bill.billClass === 'new' && <Badge>חוק חדש</Badge>}
          {bill.billClass === 'amendment' && <Badge>תיקון</Badge>}
        </span>
        <span>{[bill.status, fmtDate(bill.date)].filter(Boolean).join(' · ')}</span>
      </span>
    }
  />
);

interface ActivityTabProps {
  m: MemberDoc;
  committees: CommitteeRow[];
  substitutes: CommitteeRow[];
  filter: ActivityFilter;
  setFilter: (f: ActivityFilter) => void;
  presence: PresenceNowState;
  // A re-elected MK: the switch between their current and earlier Knesset, and (viewing the
  // earlier one) a line on what the figures are. `archived` hides what's about now (presence).
  switcher?: React.ReactNode;
  note?: string;
  archived?: boolean;
}

const ActivityTab = ({ m, committees, substitutes, filter, setFilter, presence, switcher, note, archived }: ActivityTabProps) => {
  // The Knesset this tab's figures describe: the member's latest
  const kn = m.knessetNumber || 25;
  const [comparisons, setComparisons] = useState<Comparisons | null>(null);
  const [showAllBills, setShowAllBills] = useState(false);
  const [showAllVotes, setShowAllVotes] = useState(false);
  const [voteSearch, setVoteSearch] = useState('');
  const [voteSort, setVoteSort] = useState<VoteSort>(() => {
    const v = readStored('mk_voteSort', 'date');
    return v === 'reading' || v === 'result' ? v : 'date';
  });
  const [voteDir, setVoteDir] = useState<'asc' | 'desc'>(() => (readStored('mk_voteSortDir', 'desc') === 'asc' ? 'asc' : 'desc'));

  const vs = m.voteStats && m.voteStats.participated > 0 ? m.voteStats : null;
  const hasVotes = vs !== null;

  // Party and Knesset averages and the rank, from the member index of the member's Knesset
  // (its MKs who voted, as they were then for an earlier Knesset)
  useEffect(() => {
    if (!hasVotes) return;
    let cancelled = false;
    getAllMembersOf(kn).then(all => {
      if (cancelled) return;
      const voters = all.filter(x => x.isCurrentMk === true && typeof x.attendancePct === 'number' && x.attendancePct > 0);
      const pct = (x: MemberIndexEntry) => x.attendancePct as number;
      const sorted = voters.slice().sort((a, b) => pct(b) - pct(a));
      const rank = sorted.findIndex(x => x.id === m.id) + 1;
      const party = voters.filter(x => x.partyId === m.partyId);
      setComparisons({
        partyAvg: party.length > 0 ? Math.round(sum(party.map(pct)) / party.length) : null,
        knessetAvg: voters.length > 0 ? Math.round(sum(voters.map(pct)) / voters.length) : null,
        rank: rank > 0 ? rank : null,
        total: sorted.length,
      });
    }).catch(() => { /* ok — the comparison is optional */ });
    return () => { cancelled = true; };
  }, [hasVotes, m.id, m.partyId, kn]);

  const stats = m.billStats || { total: (m.proposedBills || []).length, led: 0, passed: 0, inProgress: 0, stopped: 0 };
  const chairCount = committees.filter(c => c.isChair).length;
  const allBills = m.proposedBills || [];
  const bills = filter === 'led' ? allBills.filter(b => b.isPrimary)
    : filter === 'passed' ? allBills.filter(b => b.isPassed)
    : filter === 'inProgress' ? allBills.filter(b => b.isPrimary && !b.isPassed && !b.isStopped)
    : allBills;
  const showBills = filter === null || filter === 'all' || filter === 'led' || filter === 'passed' || filter === 'inProgress';
  const toggle = (f: ActivityFilter) => { setFilter(filter === f ? null : f); setShowAllBills(false); };

  const tiles: { key: ActivityFilter; value: React.ReactNode; label: string }[] = [];
  if (stats.total > 0) tiles.push({ key: 'all', value: stats.total, label: 'הצעות חוק' });
  if (stats.led > 0) tiles.push({ key: 'led', value: stats.led, label: 'יזם/ה' });
  if (stats.passed > 0) tiles.push({ key: 'passed', value: stats.passed, label: 'עברו' });
  if (stats.inProgress > 0) tiles.push({ key: 'inProgress', value: stats.inProgress, label: 'בתהליך' });
  if (committees.length > 0) tiles.push({ key: 'committees', value: committees.length, label: 'ועדות' });
  if (chairCount > 0) tiles.push({ key: 'chair', value: chairCount, label: 'יו״ר ועדה' });
  if (vs) tiles.push({ key: 'attendance', value: `${Math.round(vs.attendancePct)}%`, label: 'נוכחות' });
  const motionCount = m.agendaMotionCount || 0;
  if (motionCount > 0) tiles.push({ key: 'agendaMotions', value: motionCount, label: 'הצעות לסדר' });

  const notes: Partial<Record<string, string>> = {
    led: 'הצעות שחבר/ת הכנסת בין שלושת היוזמים הראשונים שלהן',
    inProgress: 'הצעות בהובלתו/ה שעדיין בתהליך',
    attendance: `נוכחות בהצבעות המליאה בלבד, מתוך כל ההצבעות בכנסת ה-${kn}. שעות בבניין הכנסת: בכרטיס "נוכחות במשכן".`,
    committees: substitutes.length > 0 ? 'חברות מלאה בלבד. ועדות שבהן הוא/היא ממלא/ת מקום מופיעות בנפרד.' : undefined,
    agendaMotions: 'הצעות לסדר היום שהגיש/ה, לבד או כחלק מהצעה כוללת עם ח״כים אחרים',
  };

  /* Bill votes: search, sort, page */
  const handleVoteSort = (key: VoteSort) => {
    if (key === voteSort) {
      const next = voteDir === 'asc' ? 'desc' : 'asc';
      setVoteDir(next);
      writeStored('mk_voteSortDir', next);
    } else {
      const dir = key === 'date' ? 'desc' : 'asc';
      setVoteSort(key);
      setVoteDir(dir);
      writeStored('mk_voteSort', key);
      writeStored('mk_voteSortDir', dir);
    }
  };
  const allVotes: MemberBillVote[] = m.billVotes || [];
  const q = voteSearch.trim();
  const votesFiltered = q ? allVotes.filter(v => (v.billTitle || '').indexOf(q) >= 0) : allVotes;
  const dir = voteDir === 'asc' ? 1 : -1;
  const votesSorted = votesFiltered.slice().sort((a, b) => {
    if (voteSort === 'reading') return dir * ((READING_ORDER[a.reading || ''] || 0) - (READING_ORDER[b.reading || ''] || 0));
    if (voteSort === 'result') {
      const ra = RESULT_ORDER[a.result] !== undefined ? RESULT_ORDER[a.result] : 1;
      const rb = RESULT_ORDER[b.result] !== undefined ? RESULT_ORDER[b.result] : 1;
      return dir * (ra - rb);
    }
    return dir * ((a.date ? new Date(a.date).getTime() : 0) - (b.date ? new Date(b.date).getTime() : 0));
  });
  const votesVisible = showAllVotes ? votesSorted : votesSorted.slice(0, VOTES_PAGE);

  // Sitting MKs and Norwegian Law ministers: the ones on the Knesset's presence board
  const presenceCard = !archived && isRanked(m) ? <PresenceCard memberId={m.id} presence={presence} /> : null;
  const nothing = allBills.length === 0 && allVotes.length === 0 && committees.length === 0 && substitutes.length === 0 && !m.agendaMotionCount;
  if (nothing && !vs) {
    return <TabBody>{switcher}{presenceCard}<Card padding={0}><EmptyState icon="document" title="אין פעילות רשומה" /></Card></TabBody>;
  }

  const diff = vs && comparisons && comparisons.partyAvg != null ? Math.round(vs.attendancePct) - comparisons.partyAvg : null;
  const passedCount = bills.filter(b => b.isPassed).length;
  const stoppedCount = bills.filter(b => b.isStopped).length;
  const progressCount = bills.length - passedCount - stoppedCount;
  const visibleBills = showAllBills ? bills : bills.slice(0, BILLS_PREVIEW);

  return (
    <TabBody>
      {switcher}
      {presenceCard}
      {tiles.length > 0 && (
        <div>
          <Card padding={space.md}>
            <CardHeader title="סיכום פעילות" meta={vs ? `כנסת ${kn} · ${vs.totalVotes.toLocaleString('he-IL')} הצבעות במליאה` : `כנסת ${kn}`} style={{ marginBottom: space.sm }} />
            {note && <div style={{ fontSize: font.caption, color: color.textTertiary, margin: `-${space.xs}px 0 ${space.sm}px` }}>{note}</div>}
            <StatTileRow>
              {tiles.map(t => (
                <StatTile key={String(t.key)} variant="flat" value={t.value} label={t.label} selected={filter === t.key} indicator="filter" onClick={() => toggle(t.key)} />
              ))}
            </StatTileRow>
          </Card>
          {filter && notes[filter] && <div style={{ marginTop: space.sm }}><Note>{notes[filter]}</Note></div>}
        </div>
      )}

      {/* Where the selected bills stand */}
      {(filter === 'all' || filter === 'led') && bills.length > 0 && (
        <Card>
          <SegmentBar
            height={10}
            label={`${passedCount} עברו, ${progressCount} בתהליך, ${stoppedCount} נעצרו`}
            segments={[
              { key: 'passed', value: passedCount, color: tone.success.fg },
              { key: 'progress', value: progressCount, color: color.primary },
              { key: 'stopped', value: stoppedCount, color: '#98a2b3' },
            ]}
          />
          <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.sm }}>
            <Legend swatch={tone.success.fg}>עברו ({passedCount})</Legend>
            <Legend swatch={color.primary}>בתהליך ({progressCount})</Legend>
            <Legend swatch="#98a2b3">נעצרו ({stoppedCount})</Legend>
          </div>
        </Card>
      )}

      {filter === 'attendance' && vs && (
        <Card>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm, flexWrap: 'wrap' }}>
            <h2 style={text.heading}>נוכחות בהצבעות</h2>
            <span style={{ fontSize: 22, fontWeight: weight.bold, color: color.primary }}>{Math.round(vs.attendancePct)}%</span>
            {diff != null && <Badge tone={diff >= 0 ? 'success' : 'danger'}><bdi dir="ltr">{diff >= 0 ? '+' : '−'}{Math.abs(diff)}%</bdi> מממוצע הסיעה</Badge>}
          </div>
          {comparisons && comparisons.rank && (
            <div style={{ fontSize: font.small, color: color.textSecondary, marginTop: space.xs }}>מקום {comparisons.rank} מתוך {comparisons.total} חברי הכנסת</div>
          )}
          <div style={{ position: 'relative', height: 10, borderRadius: radius.pill, background: color.surfaceMuted, margin: `${space.lg}px 0 ${space.sm}px` }}
            role="img" aria-label={`נוכחות ${Math.round(vs.attendancePct)}%`}>
            <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: `${Math.round(vs.attendancePct)}%`, background: color.primary, borderRadius: radius.pill }} />
            {comparisons && comparisons.knessetAvg != null && (
              <span style={{ position: 'absolute', top: -5, bottom: -5, right: `${comparisons.knessetAvg}%`, width: 3, borderRadius: 2, background: color.textTertiary, transform: 'translateX(50%)' }} />
            )}
            {comparisons && comparisons.partyAvg != null && (
              <span style={{ position: 'absolute', top: -5, bottom: -5, right: `${comparisons.partyAvg}%`, width: 3, borderRadius: 2, background: color.text, transform: 'translateX(50%)' }} />
            )}
          </div>
          {comparisons && (
            <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap' }}>
              {comparisons.partyAvg != null && <Legend swatch={color.text}>ממוצע הסיעה: {comparisons.partyAvg}%</Legend>}
              {comparisons.knessetAvg != null && <Legend swatch={color.textTertiary}>ממוצע הכנסת: {comparisons.knessetAvg}%</Legend>}
            </div>
          )}

          <div style={{ borderTop: `1px solid ${color.divider}`, marginTop: space.lg, paddingTop: space.lg }}>
            <CardHeader title="איך הצביע/ה" meta={`${vs.participated.toLocaleString('he-IL')} הצבעות`} style={{ marginBottom: space.sm }} />
            <SegmentBar
              height={10}
              label={`בעד ${vs.forPct}%, נגד ${vs.againstPct}%, נמנע ${vs.abstainPct}%`}
              segments={[
                { key: 'for', value: vs.forCount, color: tone.success.fg },
                { key: 'against', value: vs.againstCount, color: tone.danger.fg },
                { key: 'abstain', value: vs.abstainCount, color: '#98a2b3' },
              ]}
            />
            <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.sm }}>
              <Legend swatch={tone.success.fg}>בעד {vs.forPct}% ({vs.forCount.toLocaleString('he-IL')})</Legend>
              <Legend swatch={tone.danger.fg}>נגד {vs.againstPct}% ({vs.againstCount.toLocaleString('he-IL')})</Legend>
              {vs.abstainCount > 0 && <Legend swatch="#98a2b3">נמנע {vs.abstainPct}% ({vs.abstainCount.toLocaleString('he-IL')})</Legend>}
            </div>
          </div>
        </Card>
      )}

      {(filter === 'committees' || filter === 'chair') && (
        <>
          <Section title={filter === 'chair' ? 'ועדות בראשותו/ה' : 'ועדות'} meta={filter === 'chair' ? chairCount : committees.length}>
            {(filter === 'chair' ? committees.filter(c => c.isChair) : committees).length > 0
              ? <CommitteeList rows={filter === 'chair' ? committees.filter(c => c.isChair) : committees} from={m.name} />
              : <Card padding={0}><EmptyState icon="users" title="אין ועדות להצגה" /></Card>}
          </Section>
          {filter === 'committees' && substitutes.length > 0 && (
            <Section title="ממלא/ת מקום" meta={substitutes.length}>
              <CommitteeList rows={substitutes} from={m.name} />
            </Section>
          )}
        </>
      )}

      {filter === 'agendaMotions' && <MemberAgendaMotions memberId={m.id} knesset={kn} name={m.name} />}

      {showBills && bills.length > 0 && (
        <Section
          title="הצעות חוק"
          meta={filter && filter !== 'all' ? `${bills.length} מתוך ${allBills.length}` : bills.length}
          action={!showAllBills && bills.length > BILLS_PREVIEW ? <span style={{ fontSize: font.caption, fontWeight: weight.regular, color: color.textSecondary }}>{BILLS_PREVIEW} המשמעותיות ביותר</span> : undefined}
        >
          <Card padding={0}>
            {visibleBills.map((b, i) => <BillRow key={b.billId || i} bill={b} first={i === 0} from={m.name} />)}
          </Card>
          {bills.length > BILLS_PREVIEW && (
            <Button fullWidth onClick={() => setShowAllBills(!showAllBills)}>
              {showAllBills ? 'הצגת 5 המשמעותיות בלבד' : `הצגת כל ${bills.length} ההצעות`}
            </Button>
          )}
        </Section>
      )}

      {allVotes.length > 0 && (
        <Section title="הצבעות על חקיקה" meta={`כנסת ${kn} · ${q ? `${votesFiltered.length} מתוך ${allVotes.length}` : allVotes.length}`}>
          <SearchField value={voteSearch} onChange={v => { setVoteSearch(v); setShowAllVotes(false); }} placeholder="חיפוש לפי שם הצעת החוק" />
          <ChipRow>
            {VOTE_SORTS.map(s => {
              const active = voteSort === s.key;
              return (
                <Chip key={s.key} selected={active} onClick={() => handleVoteSort(s.key)}>
                  {s.label}
                  {active && <Icon name={voteDir === 'asc' ? 'sortAsc' : 'sortDesc'} size={14} strokeWidth={2.4} />}
                </Chip>
              );
            })}
          </ChipRow>
          {votesSorted.length === 0
            ? <Card padding={0}><EmptyState title="לא נמצאו הצבעות" body="נסו מילה אחרת מתוך שם החוק." /></Card>
            : (
              <Card padding={0}>
                {votesVisible.map((v, i) => {
                  const r = RESULT_BADGE[v.result] || RESULT_BADGE.abstain;
                  const meta = [v.reading ? `קריאה ${v.reading}` : 'הצבעה', v.isAccepted === undefined ? null : v.isAccepted ? 'התקבלה' : 'לא התקבלה', fmtDate(v.date)].filter(Boolean).join(' · ');
                  return (
                    <ListRow
                      key={`${v.billId}_${i}`}
                      divider={i > 0}
                      to={`/legislation/${v.billId}`}
                      state={{ from: m.name }}
                      title={v.billTitle || v.billId}
                      subtitle={meta}
                      trailing={<Badge tone={r.tone} shape="pill">{r.label}</Badge>}
                    />
                  );
                })}
              </Card>
            )}
          {votesSorted.length > VOTES_PAGE && (
            <Button fullWidth onClick={() => setShowAllVotes(!showAllVotes)}>
              {showAllVotes ? 'הצגת 10 הראשונות בלבד' : `הצגת כל ${votesSorted.length} ההצבעות`}
            </Button>
          )}
        </Section>
      )}
    </TabBody>
  );
};

/* ── שאילתות ─────────────────────────────────────────────────────────────── */

type QueryFilter = 'waiting' | 'answered' | 'withDoc' | 'ministry' | null;
const QUERIES_PAGE = 20;
const hasReply = (q: QueryDoc) => !!q.hasReplyDoc || !!q.replyDocUrl;

// `knesset`: the member's latest Knesset, whose query list (live or archived) is shown
const QueriesTab = ({ memberId, name, knesset }: { memberId: string; name: string; knesset?: number }) => {
  const [list, setList] = useState<QueryDoc[] | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [filter, setFilter] = useState<QueryFilter>(null);
  const [ministry, setMinistry] = useState<string | null>(null);
  const [visible, setVisible] = useState(QUERIES_PAGE);

  // The shared query list (also used by the queries page), so the count matches the tab's
  useEffect(() => {
    let cancelled = false;
    setError(false);
    getQueries(knesset)
      .then(all => {
        if (cancelled) return;
        setList(all.filter(q => q.memberId === memberId).sort((a, b) => (b.submitDate || '').localeCompare(a.submitDate || '')));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [memberId, knesset, reloadKey]);

  if (error) return <TabBody><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></TabBody>;
  if (!list) return <TabBody><div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><SkeletonTiles count={5} /><SkeletonList rows={5} /></div></TabBody>;
  if (list.length === 0) return <TabBody><Card padding={0}><EmptyState icon="document" title={`אין שאילתות בכנסת ה-${knesset || 25}`} /></Card></TabBody>;

  const waiting = list.filter(q => !q.replyDate).length;
  const ministryCounts: Record<string, number> = {};
  list.forEach(q => { const k = q.govMinistryName || 'לא ידוע'; ministryCounts[k] = (ministryCounts[k] || 0) + 1; });
  const ministries = Object.keys(ministryCounts).map(k => [k, ministryCounts[k]] as [string, number]).sort((a, b) => b[1] - a[1]);
  const maxMinistry = ministries.length > 0 ? ministries[0][1] : 1;

  const filtered = filter === 'waiting' ? list.filter(q => !q.replyDate)
    : filter === 'answered' ? list.filter(q => !!q.replyDate)
    : filter === 'withDoc' ? list.filter(hasReply)
    : filter === 'ministry' && ministry ? list.filter(q => (q.govMinistryName || 'לא ידוע') === ministry)
    : list;
  const pick = (f: QueryFilter) => { setFilter(filter === f ? null : f); setMinistry(null); setVisible(QUERIES_PAGE); };

  return (
    <TabBody>
      <Card padding={space.md}>
        <StatTileRow columns={3}>
          <StatTile variant="flat" value={list.length} label="סך הכל" selected={filter === null} onClick={() => pick(null)} />
          <StatTile variant="flat" value={waiting} label="ממתינות לתשובה" selected={filter === 'waiting'} indicator="filter" onClick={() => pick('waiting')} />
          <StatTile variant="flat" value={list.length - waiting} label="נענו" selected={filter === 'answered'} indicator="filter" onClick={() => pick('answered')} />
          <StatTile variant="flat" value={list.filter(hasReply).length} label="עם מסמך תשובה" selected={filter === 'withDoc'} indicator="filter" onClick={() => pick('withDoc')} />
          <StatTile variant="flat" value={ministries.length} label="משרדים" selected={filter === 'ministry'} indicator="filter" onClick={() => pick('ministry')} />
        </StatTileRow>
      </Card>

      {filter === 'ministry' && (
        <Card padding={0}>
          <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}><CardHeader title="לפי משרד" meta="בחירת משרד מסננת את הרשימה" style={{ marginBottom: 0 }} /></div>
          {ministries.map(([mName, count]) => {
            const selected = ministry === mName;
            return (
              <button
                key={mName}
                type="button"
                aria-pressed={selected}
                onClick={() => { setMinistry(selected ? null : mName); setVisible(QUERIES_PAGE); }}
                style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: `${space.sm}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs, background: selected ? color.primarySoft : 'transparent' }}
              >
                <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: font.small }}>
                  <span style={{ fontWeight: selected ? weight.bold : weight.medium, color: selected ? color.primary : color.text }}>{mName}</span>
                  <span style={{ color: selected ? color.primary : color.textSecondary }}>{count}</span>
                </span>
                <Bar value={count} max={maxMinistry} height={5} track={selected ? color.surface : color.surfaceMuted} style={{ width: '100%' }} />
              </button>
            );
          })}
        </Card>
      )}

      <Section title={ministry || 'שאילתות'} meta={filtered.length}>
        {filtered.length === 0
          ? <Card padding={0}><EmptyState title="אין שאילתות להצגה" /></Card>
          : (
            <Card padding={0}>
              {filtered.slice(0, visible).map((q, i) => {
                const answered = !!q.replyDate;
                return (
                  <ListRow
                    key={q.id}
                    divider={i > 0}
                    to={`/queries/${q.id}`}
                    state={{ from: name }}
                    title={q.name}
                    subtitle={
                      <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs, marginTop: space.xxs }}>
                        <span style={{ display: 'flex', gap: space.xs, flexWrap: 'wrap' }}>
                          <Badge tone={q.typeDesc === 'דחופה' ? 'warning' : 'info'}>{q.typeDesc || 'רגילה'}</Badge>
                          <Badge tone={answered ? 'success' : 'neutral'}>{answered ? 'נענתה' : 'ממתינה'}</Badge>
                        </span>
                        <span>{[q.govMinistryName, fmtDate(q.submitDate)].filter(Boolean).join(' · ')}</span>
                      </span>
                    }
                  />
                );
              })}
            </Card>
          )}
        {filtered.length > visible && (
          <Button fullWidth onClick={() => setVisible(visible + QUERIES_PAGE)}>הצגת עוד ({filtered.length - visible} נוספות)</Button>
        )}
      </Section>
    </TabBody>
  );
};

/* ── הוצאות ──────────────────────────────────────────────────────────────── */

const HEBREW_MONTHS = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
const VENDORS_PREVIEW = 5;

const inlineLink: React.CSSProperties = { color: color.primary, fontWeight: weight.medium };

const ExpensesTab = ({ m }: { m: MemberDoc }) => {
  const expenses = m.expenses || {};
  const years = Object.keys(expenses).sort((a, b) => Number(b) - Number(a));
  const [year, setYear] = useState(years[0]);
  const [stats, setStats] = useState<ExpenseStats | null>(null);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const [vendorsOpen, setVendorsOpen] = useState(false);
  const [allVendors, setAllVendors] = useState(false);
  const [expandedVendor, setExpandedVendor] = useState<string | null>(null);
  const [openCat, setOpenCat] = useState<string | null>(null);
  const [crossRef, setCrossRef] = useState<Record<string, VendorCrossRef | null>>({});
  const [mkInfo, setMkInfo] = useState<Record<string, MemberIndexEntry>>({});

  useEffect(() => {
    let cancelled = false;
    getExpenseStats().then(s => { if (!cancelled) setStats(s); }).catch(() => { /* ok — rank and party average are optional */ });
    return () => { cancelled = true; };
  }, []);

  const yearData = expenses[year] || expenses[years[0]];
  const items = (yearData && yearData.items) || [];

  // Vendors this year, largest first
  const vendorTotals: Record<string, number> = {};
  for (let i = 0; i < items.length; i++) {
    const v = (items[i].vendor || '').trim();
    if (v) vendorTotals[v] = (vendorTotals[v] || 0) + items[i].amount;
  }
  const vendors = Object.keys(vendorTotals).map(k => [k, vendorTotals[k]] as [string, number]).filter(e => e[1] > 0).sort((a, b) => b[1] - a[1]);
  const shownVendors = allVendors ? vendors : vendors.slice(0, VENDORS_PREVIEW);
  const shownKey = vendorsOpen ? shownVendors.map(v => v[0]).join('|') : '';

  // Which other MKs used each vendor on screen: one vendors/{id} read per vendor shown,
  // then their names from the member index
  const alive = useRef(true);
  const requestedVendors = useRef<Record<string, boolean>>({});
  useEffect(() => {
    alive.current = true;
    return () => { alive.current = false; };
  }, []);
  useEffect(() => {
    if (!shownKey) return;
    const names = shownKey.split('|').filter(n => !requestedVendors.current[n]);
    if (names.length === 0) return;
    names.forEach(n => { requestedVendors.current[n] = true; });
    Promise.all(names.map(n => getVendor(n).catch(() => null))).then(docs => {
      if (!alive.current) return;
      const next: Record<string, VendorCrossRef | null> = {};
      const ids: string[] = [];
      names.forEach((n, i) => {
        const d = docs[i];
        next[n] = d;
        if (d) ids.push(...d.mkIds);
      });
      setCrossRef(prev => ({ ...prev, ...next }));
      getMemberSummaries(ids)
        .then(info => { if (alive.current) setMkInfo(prev => ({ ...prev, ...info })); })
        .catch(() => { /* ok — names fall back to IDs */ });
    });
  }, [shownKey]);

  if (!yearData) return <TabBody><Card padding={0}><EmptyState title="אין נתוני הוצאות" /></Card></TabBody>;

  const allYearsTotal = sum(years.map(y => expenses[y].total || 0));
  const categories = Object.keys(yearData.byCategory || {}).map(k => [k, yearData.byCategory[k]] as [string, number]).sort((a, b) => b[1] - a[1]);
  const maxCat = categories.length > 0 ? Math.max(categories[0][1], 1) : 1;
  const prevYear = expenses[String(Number(year) - 1)];
  const rank = stats && stats.rankings ? stats.rankings.findIndex(r => r.memberId === m.id) + 1 : 0;
  const receiptPct = items.length > 0 ? Math.round((items.filter(it => !!it.receiptUrl).length / items.length) * 100) : null;
  const partyAvg = stats && stats.byParty && m.partyId && stats.byParty[m.partyId] ? stats.byParty[m.partyId].avg : null;

  const byMonth: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
  for (let i = 0; i < items.length; i++) {
    const parts = (items[i].date || '').split(/[-/]/);
    if (parts.length < 2) continue;
    const mi = parseInt(parts[1], 10) - 1;
    if (mi >= 0 && mi < 12) byMonth[mi] += items[i].amount;
  }
  const months = byMonth.map((total, i) => ({ month: HEBREW_MONTHS[i], total: Math.round(total) })).filter(x => x.total !== 0);
  const maxMonth = months.length > 0 ? Math.max.apply(null, months.map(x => Math.abs(x.total))) : 1;
  const maxVendor = vendors.length > 0 ? vendors[0][1] : 1;
  const maxYear = Math.max.apply(null, years.map(y => expenses[y].total || 0).concat([1]));

  const pickYear = (y: string) => { setYear(y); setOpenCat(null); setExpandedVendor(null); };

  return (
    <TabBody>
      {years.length > 1 && <TabBar label="שנה" tabs={years.map(y => ({ key: y, label: y }))} active={year} onChange={pickYear} />}

      <Card style={{ background: color.primarySoft, boxShadow: 'none' }}>
        <p style={{ margin: 0, fontSize: font.small, color: color.primaryInk, lineHeight: 1.65 }}>
          מכספי המיסים: לכל חבר/ת כנסת מוקצה תקציב שנתי ל
          <a href="https://main.knesset.gov.il/mk/pages/mkexpenses.aspx" target="_blank" rel="noopener noreferrer" style={inlineLink}>קשר עם הציבור</a>
          , הכולל שכירת משרד, טלפונים, פרסום והסברה, ייעוץ מקצועי, נסיעות, אירוח ועוד. הכנסת מחויבת לפרסם את הנתונים, וכאן אפשר לעקוב אחרי כל שקל: לפי קטגוריה, ספקים וקבלות.
        </p>
        <div style={{ display: 'flex', gap: space.sm, flexWrap: 'wrap', marginTop: space.md }}>
          <ButtonLink href="https://main.knesset.gov.il/mk/Documents/MKExpensesLaw.pdf" icon="document" variant="neutral">החוק המסדיר</ButtonLink>
          <ButtonLink href="https://main.knesset.gov.il/mk/Documents/MKExpensesServices.pdf" icon="document" variant="neutral">רשימת השירותים המותרים</ButtonLink>
        </div>
      </Card>

      <StatTileRow columns={3}>
        <StatTile value={fmtShekels(yearData.total)} label={`סה״כ ${year}`} />
        <StatTile value={fmtShekels(allYearsTotal)} label="כל השנים" />
        {rank > 0 && <StatTile value={`#${rank}`} label={`דירוג מתוך ${stats ? stats.mkCount : ''}`} title="דירוג לפי סך ההוצאות, מהגבוה לנמוך" />}
        <StatTile value={categories.length} label="קטגוריות" />
        {receiptPct !== null && <StatTile value={`${receiptPct}%`} label="עם קבלה" />}
        {partyAvg ? <StatTile value={fmtShekels(partyAvg)} label="ממוצע הסיעה" /> : null}
      </StatTileRow>

      {items.length === 0 && (
        <Card style={{ display: 'flex', alignItems: 'center', gap: space.sm }}>
          <span style={{ color: color.textTertiary }}><Icon name="alert" size={18} /></span>
          <Note>לשנת {year} יש סיכומים לפי קטגוריה בלבד, בלי פירוט עסקאות, ספקים וקבלות.</Note>
        </Card>
      )}

      {months.length > 0 && (
        <Card padding={0}>
          <Disclosure open={monthlyOpen} onToggle={() => setMonthlyOpen(!monthlyOpen)} title={`לפי חודש · ${year}`} meta={`${months.length} חודשים`} />
          {monthlyOpen && (
            <div style={{ borderTop: `1px solid ${color.divider}`, padding: space.lg, display: 'flex', flexDirection: 'column', gap: space.md }}>
              {months.map(x => (
                <div key={x.month}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                    <span style={{ fontWeight: weight.medium }}>{x.month}</span>
                    <span style={{ color: color.textSecondary }}>{fmtShekels(x.total)}</span>
                  </div>
                  <Bar value={Math.abs(x.total)} max={maxMonth} height={6} />
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {vendors.length > 0 && (
        <Card padding={0}>
          <Disclosure open={vendorsOpen} onToggle={() => { setVendorsOpen(!vendorsOpen); if (vendorsOpen) setAllVendors(false); }} title={`ספקים · ${year}`} meta={`${vendors.length} ספקים`} />
          {vendorsOpen && (
            <div style={{ borderTop: `1px solid ${color.divider}` }}>
              {shownVendors.map(([vName, vTotal], vi) => {
                const ref = crossRef[vName];
                const others = ref ? ref.mkIds.filter(id => id !== m.id) : [];
                const ministers = others.filter(id => mkInfo[id] && mkInfo[id].governmentMember && mkInfo[id].isActiveMk === false).length;
                const mks = others.length - ministers;
                const othersLabel = 'עוד ' + [
                  mks === 1 ? 'ח״כ אחד' : mks > 1 ? `${mks} ח״כים` : null,
                  ministers === 1 ? 'שר/ה אחד/ת' : ministers > 1 ? `${ministers} שרים` : null,
                ].filter(Boolean).join(' ו-');
                const expanded = expandedVendor === vName && others.length > 0;
                const pct = yearData.total > 0 ? Math.round((vTotal / yearData.total) * 100) : 0;
                const row = (
                  <>
                    <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, width: '100%' }}>
                      {others.length > 0 && <span style={{ color: color.textTertiary }}><Icon name={expanded ? 'chevronUp' : 'chevronDown'} size={16} /></span>}
                      <span style={{ flex: 1, minWidth: 0, fontSize: font.label, fontWeight: expanded ? weight.bold : weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vName}</span>
                      <span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(vTotal)} ({pct}%)</span>
                    </span>
                    {others.length > 0 && <span style={{ display: 'flex' }}><Badge tone="info">{othersLabel}</Badge></span>}
                    <Bar value={vTotal} max={maxVendor} height={6} style={{ width: '100%' }} />
                  </>
                );
                const rowStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: `${space.sm + 2}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs + 2, minHeight: 48 };
                const list = expanded && ref
                  ? [m.id].concat(others).map(id => ({ id, amount: ref.byMk[id] || 0 })).sort((a, b) => b.amount - a.amount)
                  : [];
                const maxMk = list.length > 0 ? Math.max.apply(null, list.map(x => Math.abs(x.amount)).concat([1])) : 1;
                return (
                  <div key={vName} style={{ borderTop: vi > 0 ? `1px solid ${color.divider}` : 'none' }}>
                    {others.length > 0
                      ? <button type="button" aria-expanded={expanded} onClick={() => setExpandedVendor(expanded ? null : vName)} style={{ ...buttonReset, ...rowStyle }}>{row}</button>
                      : <div style={rowStyle}>{row}</div>}
                    {expanded && ref && (
                      <div style={{ margin: `0 ${space.lg}px ${space.md}px`, background: color.surfaceMuted, borderRadius: radius.md, padding: `${space.xs}px ${space.md}px` }}>
                        {list.map((x, i) => {
                          const who = x.id === m.id ? m.name : (mkInfo[x.id] ? mkInfo[x.id].name : x.id);
                          const inner = (
                            <>
                              <span style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.small, marginBottom: space.xs }}>
                                <span style={{ color: x.id === m.id ? color.text : color.primary, fontWeight: weight.medium }}>{who}</span>
                                {x.amount !== 0 && <span style={{ color: color.textSecondary }}>{fmtShekels(x.amount)}</span>}
                              </span>
                              {x.amount !== 0 && <Bar value={Math.abs(x.amount)} max={maxMk} height={4} track={color.surface} />}
                            </>
                          );
                          const style: React.CSSProperties = { display: 'block', padding: `${space.sm}px 0`, borderTop: i > 0 ? `1px solid ${color.border}` : 'none', color: color.text, textDecoration: 'none' };
                          return x.id === m.id
                            ? <div key={x.id} style={style}>{inner}</div>
                            : <Link key={x.id} to={`/members/${x.id}`} state={{ from: m.name, stat: 'expenses' }} style={style}>{inner}</Link>;
                        })}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: `${space.sm}px 0`, borderTop: `1px solid ${color.border}`, fontSize: font.small, fontWeight: weight.bold }}>
                          <span>סה״כ מכל חברי הכנסת</span><span style={{ color: color.primary }}>{fmtShekels(sum(list.map(x => x.amount)))}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
              {vendors.length > VENDORS_PREVIEW && (
                <div style={{ padding: space.md, borderTop: `1px solid ${color.divider}` }}>
                  <Button fullWidth onClick={() => setAllVendors(!allVendors)}>
                    {allVendors ? `הצגת ${VENDORS_PREVIEW} המובילים בלבד` : `הצגת כל ${vendors.length} הספקים`}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      <Card padding={0}>
        <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}><CardHeader title={`לפי קטגוריה · ${year}`} style={{ marginBottom: 0 }} /></div>
        {categories.map(([cat, amount], ci) => {
          const pct = yearData.total > 0 ? Math.round((amount / yearData.total) * 100) : 0;
          const catItems = items.filter(it => it.category === cat);
          const canOpen = catItems.length > 0;
          const open = openCat === cat && canOpen;
          let delta: { label: string; color: string } | null = null;
          if (prevYear) {
            const before = prevYear.byCategory ? prevYear.byCategory[cat] : undefined;
            if (before !== undefined && before > 0) {
              const change = Math.round(((amount - before) / before) * 100);
              if (change > 0) delta = { label: `+${change}%`, color: tone.danger.fg };
              else if (change < 0) delta = { label: `−${Math.abs(change)}%`, color: tone.success.fg };
            } else if (before === undefined) {
              delta = { label: 'חדש', color: tone.info.fg };
            }
          }
          const head = (
            <>
              <span style={{ display: 'flex', alignItems: 'center', gap: space.sm, width: '100%' }}>
                {canOpen && <span style={{ color: color.textTertiary }}><Icon name={open ? 'chevronUp' : 'chevronDown'} size={16} /></span>}
                <span style={{ flex: 1, minWidth: 0, fontSize: font.label, fontWeight: open ? weight.bold : weight.medium, color: open ? color.primary : color.text }}>{cat}</span>
                {delta && <bdi dir="ltr" title={`לעומת ${Number(year) - 1}`} style={{ fontSize: font.caption, fontWeight: weight.bold, color: delta.color, flexShrink: 0 }}>{delta.label}</bdi>}
                <span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(amount)} ({pct}%)</span>
              </span>
              <Bar value={amount} max={maxCat} height={6} style={{ width: '100%' }} />
            </>
          );
          const headStyle: React.CSSProperties = { width: '100%', boxSizing: 'border-box', padding: `${space.sm + 2}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs + 2, minHeight: 48 };

          // The category's top vendors, then every item
          const catVendorTotals: Record<string, number> = {};
          if (open) catItems.forEach(it => { const v = (it.vendor || '').trim(); if (v) catVendorTotals[v] = (catVendorTotals[v] || 0) + it.amount; });
          const topVendors = Object.keys(catVendorTotals).map(k => [k, catVendorTotals[k]] as [string, number]).filter(e => e[1] > 0).sort((a, b) => b[1] - a[1]).slice(0, 3);

          return (
            <div key={cat} style={{ borderTop: ci > 0 ? `1px solid ${color.divider}` : 'none' }}>
              {canOpen
                ? <button type="button" aria-expanded={open} onClick={() => setOpenCat(open ? null : cat)} style={{ ...buttonReset, ...headStyle }}>{head}</button>
                : <div style={headStyle}>{head}</div>}
              {open && (
                <div style={{ padding: `0 ${space.lg}px ${space.md}px` }}>
                  {topVendors.length > 0 && (
                    <div style={{ background: color.surfaceMuted, borderRadius: radius.md, padding: space.md, marginBottom: space.sm }}>
                      <div style={{ fontSize: font.caption, color: color.textSecondary, marginBottom: space.sm }}>ספקים מובילים</div>
                      {topVendors.map(([vName, vTotal], i) => (
                        <div key={vName} style={{ marginTop: i > 0 ? space.sm : 0 }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.sm, fontSize: font.small, marginBottom: space.xs }}>
                            <span style={{ fontWeight: weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>{vName}</span>
                            <span style={{ color: color.textSecondary, flexShrink: 0 }}>{fmtShekels(vTotal)}</span>
                          </div>
                          <Bar value={vTotal} max={topVendors[0][1]} height={4} track={color.surface} />
                        </div>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: font.caption, color: color.textSecondary, margin: `${space.xs}px 0` }}>{catItems.length} רשומות</div>
                  {catItems.map((it, j) => (
                    <div key={j} style={{ display: 'flex', alignItems: 'flex-start', gap: space.sm, padding: `${space.sm}px 0`, borderTop: j > 0 ? `1px solid ${color.divider}` : 'none' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: font.small, fontWeight: weight.medium, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{it.vendor || 'ספק לא ידוע'}</div>
                        <div style={{ fontSize: font.caption, color: color.textSecondary, marginTop: space.xxs, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {[it.date, it.details].filter(Boolean).join(' · ')}
                        </div>
                        {it.receiptUrl && <div style={{ marginTop: space.xs + 2 }}><ButtonLink href={it.receiptUrl} icon="document">קבלה</ButtonLink></div>}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: space.xs, flexShrink: 0 }}>
                        {it.amount < 0 && <Badge tone="success">זיכוי</Badge>}
                        <span style={{ fontSize: font.small, fontWeight: weight.bold }}>{fmtShekels(it.amount)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </Card>

      {years.length > 1 && (
        <Card padding={0}>
          <div style={{ padding: `${space.lg}px ${space.lg}px ${space.xs}px` }}><CardHeader title="השוואה שנתית" style={{ marginBottom: 0 }} /></div>
          {years.map((y, i) => {
            const selected = y === year;
            return (
              <button
                key={y}
                type="button"
                aria-pressed={selected}
                onClick={() => pickYear(y)}
                style={{ ...buttonReset, width: '100%', boxSizing: 'border-box', minHeight: TAP, padding: `${space.sm}px ${space.lg}px`, display: 'flex', flexDirection: 'column', gap: space.xs, borderTop: i > 0 ? `1px solid ${color.divider}` : 'none' }}
              >
                <span style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: font.small }}>
                  <span style={{ fontWeight: selected ? weight.bold : weight.medium, color: selected ? color.primary : color.text }}>{y}</span>
                  <span style={{ color: selected ? color.primary : color.textSecondary }}>{fmtShekels(expenses[y].total)}</span>
                </span>
                <Bar value={expenses[y].total} max={maxYear} height={6} color={selected ? color.primary : '#98a2b3'} style={{ width: '100%' }} />
              </button>
            );
          })}
        </Card>
      )}
    </TabBody>
  );
};

/* ── שדלנות ──────────────────────────────────────────────────────────────── */

const LOBBYISTS_PREVIEW = 15;

const LobbyingTab = ({ committeeIds, name }: { committeeIds: string[]; name: string }) => {
  const [docs, setDocs] = useState<Committee[] | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const idsKey = committeeIds.join(',');

  // The lobbyist lists live only on the full committee docs, so they load with this tab
  useEffect(() => {
    let cancelled = false;
    setError(false);
    const ids = idsKey ? idsKey.split(',') : [];
    Promise.all(ids.map(getCommittee))
      .then(cs => { if (!cancelled) setDocs(cs.filter((c): c is Committee => !!c)); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [idsKey, reloadKey]);

  if (error) return <TabBody><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></TabBody>;
  if (!docs) return <TabBody><div data-skeleton="true" style={{ display: 'flex', flexDirection: 'column', gap: space.md }}><Card><SkeletonText lines={3} /></Card><SkeletonList rows={4} /></div></TabBody>;

  const byLobbyist: Record<string, { id?: string; name: string; appearances: number; isLoby99: boolean; committees: string[] }> = {};
  const orgs: Record<string, number> = {};
  let appearances = 0;
  for (const c of docs) {
    for (const l of c.lobbyists || []) {
      const k = l.id || l.name;
      if (!byLobbyist[k]) byLobbyist[k] = { id: l.id, name: l.name, appearances: 0, isLoby99: !!l.isLoby99, committees: [] };
      byLobbyist[k].appearances += l.appearances;
      if (byLobbyist[k].committees.indexOf(c.name) < 0) byLobbyist[k].committees.push(c.name);
      appearances += l.appearances;
      for (const o of l.organizations || []) orgs[o] = (orgs[o] || 0) + l.appearances;
    }
  }
  const lobbyists = Object.keys(byLobbyist).map(k => byLobbyist[k]).sort((a, b) => b.appearances - a.appearances);
  const topOrgs = Object.keys(orgs).map(k => [k, orgs[k]] as [string, number]).sort((a, b) => b[1] - a[1]).slice(0, 12);
  const loby99 = lobbyists.filter(l => l.isLoby99).length;
  const total = lobbyists.length;
  if (total === 0) return <TabBody><Card padding={0}><EmptyState icon="users" title="לא הופיעו שדלנים בוועדות שלו/ה" /></Card></TabBody>;

  const ratio = total / Math.max(docs.length, 1);
  const pressure: { label: string; tone: Tone } = ratio >= 8 ? { label: 'לחץ גבוה', tone: 'danger' } : ratio >= 4 ? { label: 'לחץ בינוני', tone: 'warning' } : { label: 'לחץ נמוך', tone: 'success' };
  const committeeRows = docs
    .filter(c => (c.lobbyists || []).length > 0)
    .map(c => ({ c, count: c.lobbyistCount || (c.lobbyists || []).length, appearances: sum((c.lobbyists || []).map(l => l.appearances)) }))
    .sort((a, b) => b.count - a.count);
  const maxCount = committeeRows.length > 0 ? committeeRows[0].count : 1;

  return (
    <TabBody>
      <Card>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.md }}>
          <div>
            <div style={{ fontSize: font.small, color: color.textSecondary }}>שדלנים שהופיעו בוועדות שלו/ה</div>
            <div style={{ fontSize: 28, fontWeight: weight.bold, color: color.primary, lineHeight: 1.2, marginTop: space.xxs }}>{total}</div>
            <div style={{ fontSize: font.small, color: color.textSecondary }}>{appearancesLabel(appearances)} {committeeRows.length === 1 ? 'בוועדה אחת' : `ב-${committeeRows.length} ועדות`}</div>
          </div>
          <Badge tone={pressure.tone} shape="pill">{pressure.label}</Badge>
        </div>
        <div style={{ marginTop: space.lg }}>
          <SegmentBar
            height={10}
            label={`לובי 99: ${loby99}, שדלנים מסחריים: ${total - loby99}`}
            segments={[{ key: 'l99', value: loby99, color: color.textTertiary }, { key: 'commercial', value: total - loby99, color: color.primary }]}
          />
          <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.sm }}>
            <Legend swatch={color.textTertiary}>לובי 99 ({loby99})</Legend>
            <Legend swatch={color.primary}>שדלנים מסחריים ({total - loby99})</Legend>
          </div>
        </div>
        <div style={{ marginTop: space.md }}><Note>שדלנים שהופיעו בוועדות שחבר/ת הכנסת חבר/ה בהן. אין זה אומר שנפגשו.</Note></div>
      </Card>

      {committeeRows.length > 0 && (
        <Section title="לפי ועדה" meta={committeeRows.length}>
          <Card padding={0}>
            {committeeRows.map((r, i) => (
              <ListRow
                key={r.c.id}
                divider={i > 0}
                to={`/committees/${r.c.id}`}
                state={{ from: name }}
                title={r.c.name}
                subtitle={
                  <span style={{ display: 'flex', flexDirection: 'column', gap: space.xs }}>
                    <span>{r.count} שדלנים · {appearancesLabel(r.appearances)}</span>
                    <Bar value={r.count} max={maxCount} height={5} />
                  </span>
                }
              />
            ))}
          </Card>
        </Section>
      )}

      {topOrgs.length > 0 && (
        <Card>
          <CardHeader title="ארגונים מובילים" meta="לפי מספר ההופעות" />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs + 2 }}>
            {topOrgs.map(([org, count]) => <FactChip key={org}>{org} <span style={{ color: color.textSecondary }}>({count})</span></FactChip>)}
          </div>
        </Card>
      )}

      <Section title="שדלנים" meta={total}>
        <Card padding={0}>
          {(showAll ? lobbyists : lobbyists.slice(0, LOBBYISTS_PREVIEW)).map((l, i) => (
            <ListRow
              key={l.id || l.name}
              divider={i > 0}
              to={l.id ? `/lobbyists/${l.id}` : undefined}
              state={{ from: name }}
              title={<span style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>{l.name}{l.isLoby99 && <Badge tone="info">לובי 99</Badge>}</span>}
              subtitle={l.committees.length > 1 ? l.committees.join(' · ') : undefined}
              trailing={<span style={{ fontSize: font.small, color: color.textSecondary, flexShrink: 0 }}>{appearancesLabel(l.appearances)}</span>}
            />
          ))}
        </Card>
        {lobbyists.length > LOBBYISTS_PREVIEW && (
          <Button fullWidth onClick={() => setShowAll(!showAll)}>{showAll ? `הצגת ${LOBBYISTS_PREVIEW} הראשונים בלבד` : `הצגת כל ${total} השדלנים`}</Button>
        )}
      </Section>
    </TabBody>
  );
};

/* ── Presence in the building (פעילות) ───────────────────────────────────── */

// This month's time in the Knesset building, from the Knesset's presence board: hours, days,
// the place in the monthly ranking (the במשכן tab) and a bar per day
const PresenceCard = ({ memberId, presence }: { memberId: string; presence: PresenceNowState }) => {
  const ym = ymOf(israelToday());
  const now = presence.now;
  const { month, error, retry } = usePresenceMonth(ym, (now && now.changedAt) || '');
  const [memberIds, setMemberIds] = useState<string[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAllMembers()
      .then(all => { if (!cancelled) setMemberIds(all.filter(isRanked).map(x => x.id)); })
      .catch(() => { if (!cancelled) setMemberIds([]); });
    return () => { cancelled = true; };
  }, []);

  const first = ym === PRESENCE_FIRST_MONTH;
  const start = `${parseInt(PRESENCE_START.slice(8, 10), 10)}.${parseInt(PRESENCE_START.slice(5, 7), 10)}`;
  const header = <CardHeader title="נוכחות במשכן" meta={first ? `${monthLabel(ym)} · מ-${start}` : monthLabel(ym)} style={{ marginBottom: space.sm }} />;

  if (month === undefined && error) {
    return <Card padding={0}><ErrorState onRetry={retry} message="לא הצלחנו לטעון את נתוני הנוכחות במשכן." /></Card>;
  }
  if (month === undefined || (now === undefined && !presence.error) || memberIds === null) {
    return <Card>{header}<div data-skeleton="true"><SkeletonTiles count={3} /></div></Card>;
  }

  const live = now || null;
  const mine = monthTotals(month, ym, live, presence.clock, memberId);
  if (mine.seconds === 0) {
    return (
      <Card>
        {header}
        <div style={{ fontSize: font.small, color: color.textSecondary, lineHeight: 1.5 }}>
          {first ? `עדיין לא נרשמה שהות במשכן מאז ${start}.` : 'עדיין לא נרשמה שהות במשכן החודש.'}
          {' '}<Link to="/parties?tab=presence&view=month" style={inlineLink}>לדירוג החודשי</Link>
        </div>
      </Card>
    );
  }

  const ids = rankingIds(memberIds, month, live, ym);
  const rank = hoursRank(memberId, ids, month, ym, live, presence.clock);
  const hereNow = !!live && !isPresenceStale(live, presence.clock) && !!live.present[memberId];
  const days = presenceDays(ym);
  const today = israelDay(presence.clock);
  const max = Math.max(8 * 3600, ...days.map(d => mine.byDay[d] || 0));
  const longest = days.reduce((best, d) => ((mine.byDay[d] || 0) > (mine.byDay[best] || 0) ? d : best), days[0]);
  const dm = (d: string) => `${parseInt(d, 10)}.${parseInt(ym.slice(5, 7), 10)}`;

  return (
    <Card>
      {header}
      <StatTileRow columns={3}>
        <StatTile variant="flat" value={hoursLabel(mine.seconds)} label="שעות" />
        <StatTile variant="flat" value={mine.days} label={mine.days === 1 ? 'יום' : 'ימים'} />
        <StatTile
          variant="flat"
          to="/parties?tab=presence&view=month"
          value={rank ? <span>{rank}<span style={{ fontSize: font.small, fontWeight: weight.medium, color: color.textSecondary }}> / {ids.length}</span></span> : '—'}
          label="מקום בדירוג"
        />
      </StatTileRow>
      <div
        role="img"
        aria-label={`שעות במשכן לפי יום ב${monthLabel(ym)}: ${mine.days} ${mine.days === 1 ? 'יום' : 'ימים'} עם נוכחות, היום הארוך ביותר ${hoursLabel(mine.byDay[longest] || 0)} שעות ב-${dm(longest)}`}
        style={{ marginTop: space.lg }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: days.length > 26 ? 2 : 3, height: 84, borderBottom: `1px solid ${color.border}` }}>
          {days.map(d => {
            const s = mine.byDay[d] || 0;
            const future = d > today;
            return (
              <span
                key={d}
                title={future ? undefined : `${dm(d)}: ${hoursLabel(s)}`}
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  height: future || s === 0 ? 3 : Math.max(3, Math.round((s / max) * 80)),
                  borderRadius: '3px 3px 0 0',
                  background: future ? color.divider : s === 0 ? color.border : d === today && hereNow ? color.present : color.primary,
                }}
              />
            );
          })}
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: font.caption, color: color.textTertiary, marginTop: space.xs }}>
          <span>{dm(days[0])}</span>
          <span>{dm(days[days.length - 1])}</span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: space.md, flexWrap: 'wrap', marginTop: space.sm }}>
        <Legend swatch={color.primary}>שעות ביום</Legend>
        {hereNow && <Legend swatch={color.present}>היום, עד עכשיו</Legend>}
      </div>
      <div style={{ fontSize: font.caption, color: color.textTertiary, marginTop: space.md, lineHeight: 1.5 }}>
        {PRESENCE_SOURCE} · דיוק של כדקה
      </div>
    </Card>
  );
};

/* ── Desktop portrait ────────────────────────────────────────────────────── */

// Desktop profile panel: the whole official photo in a 3:4 frame (Avatar shape="portrait")
// rather than a crop that keeps only the face (owner's choice)
const PORTRAIT_W = 132;
const PORTRAIT_H = 176;

/* ── Page ────────────────────────────────────────────────────────────────── */

const MemberDetailPage = () => {
  const { memberId = '' } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isDesktop } = useBreakpoint();
  const [params, setParams] = useUrlParams();
  const incomingStat = (location.state as { stat?: string } | null)?.stat;
  // undefined while loading, null when there is no such member
  const [member, setMember] = useState<MemberDoc | null | undefined>(undefined);
  const [summary, setSummary] = useState<MemberIndexEntry | null>(null);
  const [parties, setParties] = useState<Party[]>([]);
  const [committeeList, setCommitteeList] = useState<Committee[] | null>(null);
  const [error, setError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>(() => statToFilter(incomingStat));
  // In the Knesset building now: the header tag, and the live part of the פעילות card
  const presence = usePresenceNow();
  const scope = useScope();
  // A re-elected MK's record of the previous Knesset (knessets/{k}/members), for the activity
  // tab's Knesset switch (?k=)
  const earlierK = previousKnesset(scope);
  const memberKnesset = member ? member.knessetNumber : undefined;
  const [earlierRecord, setEarlierRecord] = useState<MemberKnessetRecord | null>(null);
  useEffect(() => {
    setEarlierRecord(null);
    if (earlierK === null || typeof memberKnesset !== 'number' || memberKnesset <= earlierK) return;
    let cancelled = false;
    getMemberKnessetRecord(memberId, earlierK).then(r => { if (!cancelled) setEarlierRecord(r); }).catch(() => { /* ok — no switch */ });
    return () => { cancelled = true; };
  }, [memberId, earlierK, memberKnesset]);

  // The full doc for the tabs; the member index (usually cached already) draws the header sooner
  useEffect(() => {
    let cancelled = false;
    setError(false);
    getMember(memberId).then(d => { if (!cancelled) setMember(d); }).catch(() => { if (!cancelled) setError(true); });
    getMemberIndex().then(idx => { if (!cancelled && idx) setSummary(idx[memberId] || null); }).catch(() => { /* ok */ });
    getParties().then(ps => { if (!cancelled) setParties(ps); }).catch(() => { /* ok — the party name is optional */ });
    getCommitteeList().then(cs => { if (!cancelled) setCommitteeList(cs); }).catch(() => { if (!cancelled) setCommitteeList([]); });
    return () => { cancelled = true; };
  }, [memberId, reloadKey]);

  // Scroll restoration: save on unmount, restore once the member has loaded
  const restored = useRef(false);
  // Where the tab bar sits in the page before it sticks (phone), to scroll back to it
  const tabsAnchorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const key = `mk_scroll_${memberId}`;
    return () => { try { sessionStorage.setItem(key, String(window.scrollY)); } catch { /* ok */ } };
  }, [memberId]);
  useEffect(() => {
    if (restored.current || !member) return;
    restored.current = true;
    try {
      const key = `mk_scroll_${memberId}`;
      const saved = sessionStorage.getItem(key);
      if (saved) { window.scrollTo(0, parseInt(saved, 10) || 0); sessionStorage.removeItem(key); }
    } catch { /* ok */ }
  }, [member, memberId]);

  if (error && !member) {
    return <Layout><div style={{ padding: space.lg }}><Card padding={0}><ErrorState onRetry={() => setReloadKey(k => k + 1)} /></Card></div></Layout>;
  }
  if (member === null) {
    return (
      <Layout>
        <div style={{ padding: space.lg }}>
          <Card padding={0}>
            <EmptyState icon="user" title="חבר/ת הכנסת לא נמצא/ה" body="ייתכן שהקישור שגוי." action={<Button onClick={() => navigate('/parties?tab=members', { replace: true })}>לכל חברי הכנסת</Button>} />
          </Card>
        </div>
      </Layout>
    );
  }

  const who: HeaderSource | null = member || summary;
  const partyId = who ? who.partyId : undefined;
  const party = parties.find(p => p.id === partyId) || null;
  const partyName = party ? shortPartyName(party.name) : '';
  const name = who ? who.name : '';

  /* Committees: full memberships count; substitute roles are listed apart (owner rule) */
  const committeeById: Record<string, Committee> = {};
  (committeeList || []).forEach(c => { committeeById[c.id] = c; });
  const toRow = (id: string): CommitteeRow | null => {
    const c = committeeById[id];
    if (!c) return null;
    return { id, name: c.name, type: c.type, isChair: c.currentChairId === memberId, isCurrent: c.isCurrent, lobbyistCount: c.lobbyistCount || (c.lobbyists || []).length };
  };
  const byChairThenName = (a: CommitteeRow, b: CommitteeRow) => (a.isChair !== b.isChair ? (a.isChair ? -1 : 1) : a.name.localeCompare(b.name, 'he'));
  const fullIds = (member && member.committeeIds) || (summary && summary.committeeIds) || [];
  const subIds = ((member && member.substituteCommitteeIds) || (summary && summary.substituteCommitteeIds) || []).filter(id => fullIds.indexOf(id) < 0);
  const committees = fullIds.map(toRow).filter((c): c is CommitteeRow => !!c).sort(byChairThenName);
  const substitutes = subIds.map(toRow).filter((c): c is CommitteeRow => !!c).sort(byChairThenName);

  /* The previous Knesset's record (re-elected MKs): the activity tab can show it instead */
  const viewEarlier = !!earlierRecord && earlierK !== null && params.get('k') === String(earlierK);
  const earlierFull = (earlierRecord && earlierRecord.committeeIds) || [];
  const earlierCommittees = earlierFull.map(toRow).filter((c): c is CommitteeRow => !!c).sort(byChairThenName);
  const earlierSubstitutes = ((earlierRecord && earlierRecord.substituteCommitteeIds) || []).filter(id => earlierFull.indexOf(id) < 0)
    .map(toRow).filter((c): c is CommitteeRow => !!c).sort(byChairThenName);
  const earlierParty = earlierRecord && earlierRecord.partyId ? parties.find(p => p.id === earlierRecord.partyId) : undefined;
  const earlierNote = earlierK !== null
    ? `${earlierParty ? `בסיעת ${shortPartyName(earlierParty.name)} · ` : ''}כפי שהיה בסיום הכנסת ה-${earlierK}`
    : undefined;

  /* Tabs: in the URL. Without one, the caller's `stat` picks it. */
  const queryCount = (member && member.queryCount) || (summary && summary.queryCount) || 0;
  const available: Record<MkTab, boolean> = {
    bio: true,
    career: true,
    activity: true,
    queries: queryCount > 0,
    // Until the committee list loads, trust a lobbying link
    lobbying: committeeList ? committees.some(c => c.lobbyistCount > 0) : true,
    expenses: !!member && !!member.expenses && Object.keys(member.expenses).length > 0,
  };
  const defaultTab = statToTab(incomingStat);
  const requested = params.get('tab') as MkTab | null;
  let activeTab: MkTab = requested && TAB_KEYS.indexOf(requested) >= 0 ? requested : defaultTab;
  if (member && !available[activeTab]) activeTab = 'bio';
  const setActiveTab = (tab: MkTab) => {
    setParams(prev => {
      const next = new URLSearchParams(prev);
      if (tab === defaultTab) next.delete('tab'); else next.set('tab', tab);
      return next;
    });
    // Phone: switching tabs while scrolled down starts the new tab at its top, right under the
    // stuck tab bar, instead of somewhere in the middle of it
    const anchor = tabsAnchorRef.current;
    if (!isDesktop && anchor) {
      const top = anchor.getBoundingClientRect().top + window.scrollY - MOBILE_TOP_BAR_HEIGHT;
      if (window.scrollY > top) window.scrollTo({ top });
    }
  };

  const tabs: { key: MkTab; label: string; count?: number }[] = [
    { key: 'bio', label: 'פרופיל' },
    { key: 'career', label: 'קריירה' },
    { key: 'activity', label: 'פעילות' },
  ];
  if (available.queries) tabs.push({ key: 'queries', label: 'שאילתות' });
  if (committeeList && available.lobbying) tabs.push({ key: 'lobbying', label: 'שדלנות' });
  if (available.expenses) tabs.push({ key: 'expenses', label: 'הוצאות' });

  /* Header */
  const avatarSize = 96;
  const avatar = isDesktop
    // The 240px thumbnail covers the 132px frame at the desktop zoom; a skeleton until the MK loads
    ? (who ? <Avatar name={name || '?'} src={photoFor(who, avatarSize)} size={PORTRAIT_W} shape="portrait" /> : <Skeleton width={PORTRAIT_W} height={PORTRAIT_H} rounded={radius.lg} />)
    : <Avatar name={name || '?'} src={who ? photoFor(who, avatarSize) : undefined} size={avatarSize} shape="square" ring />;
  const isPlainMk = (title: string | undefined) => !title || title === 'חבר/ת כנסת' || title === 'חבר כנסת' || title === 'חברת כנסת';
  const roles = who ? (who.roles || []).filter((r: MemberRole) => !isPlainMk(r.title)) : [];
  const departed = !!who && who.isCurrentMk === false && !who.governmentMember;
  const norwegian = !!who && who.isActiveMk === false && !!who.governmentMember;
  const presentSince = presence.now && !isPresenceStale(presence.now, presence.clock) ? presence.now.present[memberId] : undefined;

  const headerInfo = who ? (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: space.sm }}>
      <div>
        <h1 style={text.entityTitle}>{who.name}</h1>
        {who.profession && <div style={{ fontSize: font.small, color: color.textSecondary, marginTop: space.xxs }}>{who.profession.trim()}</div>}
      </div>
      {(partyName || departed || norwegian || who.plenumSeat || presentSince) && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: space.xs + 2, flexWrap: 'wrap' }}>
          {partyId && partyName && (
            // The visible pill is 32px; the link's padding makes the tap target 44px
            <Link to={`/parties/${partyId}`} state={{ from: who.name }} style={{ display: 'inline-flex', padding: '6px 0', textDecoration: 'none' }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: space.xs + 2, height: 32, padding: `0 ${space.md}px`, borderRadius: radius.pill, background: color.primarySoft, color: color.primary, fontSize: font.small, fontWeight: weight.medium }}>
                {party && party.color && <span style={{ width: 8, height: 8, borderRadius: '50%', background: party.color, flexShrink: 0 }} />}
                {partyName}
              </span>
            </Link>
          )}
          {departed && <Badge shape="pill">סיים/ה כהונה</Badge>}
          {norwegian && <span title="ויתר/ה על המושב בכנסת לפי החוק הנורווגי"><Badge shape="pill">נורווגי</Badge></span>}
          {who.plenumSeat && <Badge shape="pill">מושב {who.plenumSeat}</Badge>}
          {presentSince && <Badge tone="success" shape="pill" dot={color.present}>במשכן מאז {sinceLabel(presentSince)}</Badge>}
        </div>
      )}
      {roles.length > 0
        ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: space.xs, justifyContent: 'center' }}>{roles.map((r, i) => <RoleTag key={i} role={r} size="small" />)}</div>
        : !isPlainMk(who.role) ? <div style={{ fontSize: font.small, color: color.textSecondary }}>{who.role}</div> : null}
    </div>
  ) : (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.sm }}>
      <Skeleton width="55%" height={24} />
      <Skeleton width="35%" height={14} />
    </div>
  );

  const side = isDesktop
    ? <Card><div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: space.md }}>{avatar}{headerInfo}</div></Card>
    : (
      <DetailHeader
        fallback={partyId ? `/parties/${partyId}` : '/parties'}
        fallbackLabel={partyName || 'הכנסת'}
        avatar={avatar}
        bandColor={(party && party.color) || color.primaryInk}
      >
        {headerInfo}
      </DetailHeader>
    );

  // Equal-width underline tabs. Phone: the bar sticks under the top bar while the tab scrolls.
  const tabsBar = (
    <>
      {!isDesktop && <div ref={tabsAnchorRef} aria-hidden="true" />}
      <div style={isDesktop
        ? { padding: `0 0 ${space.lg}px` }
        : { position: 'sticky', top: MOBILE_TOP_BAR_HEIGHT, zIndex: 40, background: 'rgba(238,242,247,0.94)', padding: `${space.md}px ${space.lg}px ${space.sm}px` }}>
        {member
          ? <TabBar variant="underline" label="לשוניות חבר הכנסת" tabs={tabs} active={activeTab} onChange={setActiveTab} />
          : <Skeleton height={52} rounded={radius.lg} />}
      </div>
    </>
  );

  let content: React.ReactNode;
  if (!member) {
    content = (
      <div data-skeleton="true" style={{ padding: tabPadding(isDesktop), display: 'flex', flexDirection: 'column', gap: space.md }}>
        <SkeletonTiles count={4} />
        <Card><SkeletonText lines={4} /></Card>
        <Card><SkeletonText lines={3} /></Card>
      </div>
    );
  } else if (activeTab === 'career') {
    content = <CareerTab m={member} />;
  } else if (activeTab === 'activity') {
    const knessetSwitch = earlierRecord && earlierK !== null && typeof member.knessetNumber === 'number' ? (
      <TabBar
        label="כנסת"
        tabs={[
          { key: String(member.knessetNumber), label: `הכנסת ה-${member.knessetNumber}` },
          { key: String(earlierK), label: `הכנסת ה-${earlierK}` },
        ]}
        active={viewEarlier ? String(earlierK) : String(member.knessetNumber)}
        onChange={key => setParams(prev => {
          const next = new URLSearchParams(prev);
          if (key === String(earlierK)) next.set('k', key); else next.delete('k');
          return next;
        })}
      />
    ) : undefined;
    content = viewEarlier && earlierRecord && earlierK !== null
      ? <ActivityTab key="earlier" m={{ ...member, ...earlierRecord, agendaMotionCount: earlierRecord.agendaMotionCount, knessetNumber: earlierK } as MemberDoc} committees={earlierCommittees} substitutes={earlierSubstitutes} filter={activityFilter} setFilter={setActivityFilter} presence={presence} switcher={knessetSwitch} note={earlierNote} archived />
      : <ActivityTab key="current" m={member} committees={committees} substitutes={substitutes} filter={activityFilter} setFilter={setActivityFilter} presence={presence} switcher={knessetSwitch} />;
  } else if (activeTab === 'queries') {
    content = <QueriesTab memberId={memberId} name={member.name} knesset={member.knessetNumber} />;
  } else if (activeTab === 'lobbying') {
    content = committeeList
      ? <LobbyingTab committeeIds={committees.map(c => c.id)} name={member.name} />
      : <TabBody><SkeletonList rows={4} /></TabBody>;
  } else if (activeTab === 'expenses') {
    content = <ExpensesTab m={member} />;
  } else {
    content = <ProfileTab m={member} />;
  }

  const breadcrumbs = [
    ...(partyId && partyName ? [{ label: partyName, to: `/parties/${partyId}` }] : []),
    { label: name },
  ];

  return (
    <Layout width="full" breadcrumbs={breadcrumbs}>
      <PageContainer>
        {/* Desktop: the tabs sit above the main column, so the profile panel lines up with the content below them */}
        <TwoPane side={side} sideWidth={300} mainHeader={tabsBar}>
          {content}
        </TwoPane>
      </PageContainer>
    </Layout>
  );
};

export default MemberDetailPage;
